import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  Download,
  LockKeyhole,
  LogOut,
  RefreshCw,
  Search,
} from 'lucide-react'
import {
  exportAdminBetaRegistrations,
  getAdminBetaRegistrations,
  getAdminBetaStats,
} from '../../api/adminBetaApi'
import type {
  AdminBetaBreakdownItem,
  AdminBetaFilters,
  AdminBetaRegistration,
  AdminBetaRegistrationsResponse,
  AdminBetaStatsResponse,
} from '../../models/AdminBeta'

const adminKeyStorageKey = 'tonesignature.adminKey'
const defaultFilters: AdminBetaFilters = {
  search: '',
  country: '',
  hardware: '',
  page: 1,
  pageSize: 25,
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

function BreakdownList({
  items,
  total,
  emptyLabel,
}: {
  items: AdminBetaBreakdownItem[]
  total: number
  emptyLabel: string
}) {
  if (items.length === 0) {
    return <p className="admin-empty-line">{emptyLabel}</p>
  }

  return <div className="admin-breakdown-list">
    {items.map((item) => {
      const percentage = total > 0 ? Math.round((item.count / total) * 100) : 0
      return <div className="admin-breakdown-row" key={item.label}>
        <div>
          <span>{item.label}</span>
          <b>{item.count} ({percentage}%)</b>
        </div>
        <i>
          <em style={{ width: `${percentage}%` }}/>
        </i>
      </div>
    })}
  </div>
}

function RegistrationRow({ registration }: { registration: AdminBetaRegistration }) {
  return <tr>
    <td>{formatDate(registration.createdAtUtc)}</td>
    <td>{registration.email}</td>
    <td>{registration.name || '-'}</td>
    <td>{registration.country}</td>
    <td>{registration.guitarHardware}</td>
    <td><span className="admin-status">{registration.status}</span></td>
  </tr>
}

export function AdminBetaDashboard() {
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem(adminKeyStorageKey) ?? '')
  const [keyInput, setKeyInput] = useState('')
  const [filters, setFilters] = useState<AdminBetaFilters>(defaultFilters)
  const [stats, setStats] = useState<AdminBetaStatsResponse | null>(null)
  const [registrations, setRegistrations] = useState<AdminBetaRegistrationsResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isExporting, setIsExporting] = useState(false)
  const [error, setError] = useState('')

  const hardwareOptions = useMemo(
    () => stats?.hardwareBreakdown.map((item) => item.label) ?? [],
    [stats],
  )
  const countryOptions = useMemo(
    () => stats?.countryBreakdown.map((item) => item.label) ?? [],
    [stats],
  )

  const loadDashboard = useCallback(async () => {
    if (!adminKey) {
      return
    }

    setIsLoading(true)
    setError('')

    try {
      const [statsResponse, registrationsResponse] = await Promise.all([
        getAdminBetaStats(adminKey),
        getAdminBetaRegistrations(adminKey, filters),
      ])
      setStats(statsResponse)
      setRegistrations(registrationsResponse)
    } catch (loadError) {
      const message = loadError instanceof Error
        ? loadError.message
        : 'The admin dashboard could not load. Please try again.'
      setError(message)
      if (message.includes('admin key')) {
        sessionStorage.removeItem(adminKeyStorageKey)
        setAdminKey('')
      }
    } finally {
      setIsLoading(false)
    }
  }, [adminKey, filters])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      void loadDashboard()
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [loadDashboard])

  useEffect(() => {
    document.title = 'Founding Beta Dashboard - ToneSignature'
  }, [])

  function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const trimmedKey = keyInput.trim()
    if (!trimmedKey) {
      setError('Enter the admin key to continue.')
      return
    }

    sessionStorage.setItem(adminKeyStorageKey, trimmedKey)
    setAdminKey(trimmedKey)
    setKeyInput('')
    setError('')
  }

  function handleLogout() {
    sessionStorage.removeItem(adminKeyStorageKey)
    setAdminKey('')
    setStats(null)
    setRegistrations(null)
    setFilters(defaultFilters)
  }

  function updateFilters(nextFilters: Partial<AdminBetaFilters>) {
    setFilters((current) => ({
      ...current,
      ...nextFilters,
      page: nextFilters.page ?? 1,
    }))
  }

  async function handleExport() {
    if (!adminKey) {
      return
    }

    setIsExporting(true)
    setError('')

    try {
      const blob = await exportAdminBetaRegistrations(adminKey, filters)
      downloadBlob(blob, 'tonesignature-beta-registrations.csv')
    } catch (exportError) {
      setError(exportError instanceof Error ? exportError.message : 'The CSV export could not be created.')
    } finally {
      setIsExporting(false)
    }
  }

  if (!adminKey) {
    return <main className="admin-page admin-login-page">
      <form className="admin-login-card" onSubmit={handleLogin}>
        <span className="admin-lock"><LockKeyhole size={20}/></span>
        <h1>Founding Beta Dashboard</h1>
        <p>Enter the admin key to monitor early access registrations.</p>
        <label>
          Admin Key
          <input
            autoComplete="off"
            onChange={(event) => setKeyInput(event.target.value)}
            type="password"
            value={keyInput}
          />
        </label>
        {error ? <p className="admin-error">{error}</p> : null}
        <button className="button" type="submit">Login</button>
      </form>
    </main>
  }

  const totalRegistrations = stats?.totalRegistrations ?? 0
  const items = registrations?.items ?? []
  const currentPage = registrations?.page ?? filters.page
  const totalPages = registrations?.totalPages ?? 0

  return <main className="admin-page">
    <section className="admin-shell">
      <div className="admin-topbar">
        <div>
          <h1>Founding Beta Dashboard</h1>
          <p>Monitor early access registrations</p>
        </div>
        <div className="admin-actions">
          <button className="button ghost small" disabled={isLoading} onClick={loadDashboard} type="button">
            <RefreshCw size={16}/> Refresh
          </button>
          <button className="button ghost small" onClick={handleLogout} type="button">
            <LogOut size={16}/> Logout
          </button>
        </div>
      </div>

      {error ? <p className="admin-error">{error}</p> : null}

      <div className="admin-card-grid">
        <article>
          <span>Total Registrations</span>
          <b>{stats?.totalRegistrations ?? '-'}</b>
        </article>
        <article>
          <span>Today</span>
          <b>{stats?.registrationsToday ?? '-'}</b>
        </article>
        <article>
          <span>Last 7 Days</span>
          <b>{stats?.registrationsLast7Days ?? '-'}</b>
        </article>
        <article>
          <span>Countries</span>
          <b>{stats?.countriesCount ?? '-'}</b>
        </article>
      </div>

      <div className="admin-filters">
        <label className="admin-search">
          <Search size={16}/>
          <input
            onChange={(event) => updateFilters({ search: event.target.value })}
            placeholder="Search"
            type="search"
            value={filters.search}
          />
        </label>
        <select
          aria-label="Hardware"
          onChange={(event) => updateFilters({ hardware: event.target.value })}
          value={filters.hardware}
        >
          <option value="">All hardware</option>
          {hardwareOptions.map((hardware) => <option key={hardware} value={hardware}>{hardware}</option>)}
        </select>
        <select
          aria-label="Country"
          onChange={(event) => updateFilters({ country: event.target.value })}
          value={filters.country}
        >
          <option value="">All countries</option>
          {countryOptions.map((country) => <option key={country} value={country}>{country}</option>)}
        </select>
        <button className="button ghost small" onClick={() => setFilters(defaultFilters)} type="button">
          Clear Filters
        </button>
        <button className="button small" disabled={isExporting} onClick={handleExport} type="button">
          <Download size={16}/> {isExporting ? 'Exporting' : 'Export CSV'}
        </button>
      </div>

      <div className="admin-table-panel">
        {isLoading ? <p className="admin-state">Loading beta registrations...</p> : null}
        {!isLoading && items.length === 0 ? <p className="admin-state">No beta registrations yet.</p> : null}
        {!isLoading && items.length > 0 ? <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Email</th>
                <th>Name</th>
                <th>Country</th>
                <th>Hardware</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((registration) => <RegistrationRow key={registration.id} registration={registration}/>)}
            </tbody>
          </table>
        </div> : null}
      </div>

      <div className="admin-pagination">
        <button
          className="button ghost small"
          disabled={currentPage <= 1 || isLoading}
          onClick={() => updateFilters({ page: currentPage - 1 })}
          type="button"
        >
          Previous
        </button>
        <span>Page {currentPage} of {Math.max(totalPages, 1)}</span>
        <button
          className="button ghost small"
          disabled={totalPages === 0 || currentPage >= totalPages || isLoading}
          onClick={() => updateFilters({ page: currentPage + 1 })}
          type="button"
        >
          Next
        </button>
      </div>

      <div className="admin-breakdowns">
        <section>
          <h2>Hardware Breakdown</h2>
          <BreakdownList
            emptyLabel="No hardware data yet."
            items={stats?.hardwareBreakdown ?? []}
            total={totalRegistrations}
          />
        </section>
        <section>
          <h2>Country Breakdown</h2>
          <BreakdownList
            emptyLabel="No country data yet."
            items={stats?.countryBreakdown ?? []}
            total={totalRegistrations}
          />
        </section>
      </div>
    </section>
  </main>
}
